import type { MailRibbonGroup } from '../types/mailRibbonGroup';
import type { TabScalingAndLayoutConfig } from '../types/tabScalingAndLayoutConfig';

/**
 * Moves group ordering and controls that still match the source defaults to the target defaults,
 * while preserving any user-customized ordering or controls.
 */
export function reconcileWin32HomeTabConfig(
    homeTab: TabScalingAndLayoutConfig,
    sourceDefault: TabScalingAndLayoutConfig,
    targetDefault: TabScalingAndLayoutConfig
): TabScalingAndLayoutConfig {
    const layout = reconcileLayout(homeTab.layout, sourceDefault.layout, targetDefault.layout);
    const staticGroupIdOrdering =
        arraysEqual(homeTab.staticGroupIdOrdering, sourceDefault.staticGroupIdOrdering) &&
        !arraysEqual(homeTab.staticGroupIdOrdering, targetDefault.staticGroupIdOrdering)
            ? [...targetDefault.staticGroupIdOrdering]
            : homeTab.staticGroupIdOrdering;

    if (layout === homeTab.layout && staticGroupIdOrdering === homeTab.staticGroupIdOrdering) {
        return homeTab;
    }

    return {
        ...homeTab,
        layout,
        staticGroupIdOrdering,
    };
}

function reconcileLayout(
    layout: MailRibbonGroup[],
    sourceDefaultLayout: MailRibbonGroup[],
    targetDefaultLayout: MailRibbonGroup[]
): MailRibbonGroup[] {
    const sourceGroupIds = sourceDefaultLayout.map(group => group.groupId);
    const targetGroupIds = targetDefaultLayout.map(group => group.groupId);
    const currentGroupIds = layout.map(group => group.groupId);
    const shouldUpdateGroupOrdering =
        arraysEqual(currentGroupIds, sourceGroupIds) &&
        !arraysEqual(currentGroupIds, targetGroupIds);
    const currentGroups = new Map(layout.map(group => [group.groupId, group]));
    const sourceGroups = new Map(sourceDefaultLayout.map(group => [group.groupId, group]));
    const targetGroups = new Map(targetDefaultLayout.map(group => [group.groupId, group]));
    const orderedGroups = shouldUpdateGroupOrdering
        ? targetGroupIds
              .map(groupId => {
                  const currentGroup = currentGroups.get(groupId);
                  if (currentGroup) {
                      return currentGroup;
                  }

                  const targetGroup = targetGroups.get(groupId);
                  return targetGroup
                      ? {
                            ...targetGroup,
                            controlIds: [...targetGroup.controlIds],
                        }
                      : undefined;
              })
              .filter(isDefined)
        : layout;
    let didUpdateControls = false;

    const updatedLayout = orderedGroups.map(group => {
        const sourceGroup = sourceGroups.get(group.groupId);
        const targetGroup = targetGroups.get(group.groupId);

        if (
            sourceGroup &&
            targetGroup &&
            arraysEqual(group.controlIds, sourceGroup.controlIds) &&
            !arraysEqual(group.controlIds, targetGroup.controlIds)
        ) {
            didUpdateControls = true;
            return {
                ...group,
                controlIds: [...targetGroup.controlIds],
            };
        }

        return group;
    });

    return shouldUpdateGroupOrdering || didUpdateControls ? updatedLayout : layout;
}

function arraysEqual<T>(left: T[], right: T[]): boolean {
    return left.length === right.length && left.every((value, index) => value === right[index]);
}

function isDefined<T>(value: T | undefined): value is T {
    return value !== undefined;
}
